import { platformApi } from '@/apis';
import { featureSortFn, filterType, reviveDate } from '@/utils';
import { captureMessage } from '@sentry/react';
import { parseServerSentEvents } from 'parse-sse';
import { useEffect, useState } from 'react';

export function useStreamingFeatureCollection(
  route,
  search,
  sort,
  filter,
  enabled = true,
) {
  const [featureCollection, setFeatureCollection] = useState({
    type: 'FeatureCollection',
    features: [],
  });

  useEffect(() => {
    const ctrl = new AbortController();

    async function initiateStream() {
      try {
        const response = await platformApi.get(`sse/${route}`, {
          signal: ctrl.signal,
          headers: { accept: 'text/event-stream' },
        });

        for await (const msg of parseServerSentEvents(response)) {
          if (msg.data) {
            const newFeature = JSON.parse(msg.data, reviveDate);

            setFeatureCollection((featureCollection) => {
              const alreadyExists = featureCollection.features.some(
                (feature) => feature.id === newFeature.id,
              );

              if (alreadyExists && msg.event === 'initial') {
                return featureCollection;
              }

              return {
                ...featureCollection,
                features: alreadyExists
                  ? featureCollection.features.map((feature) =>
                      feature.id === newFeature.id ? newFeature : feature,
                    )
                  : [...featureCollection.features, newFeature],
              };
            });
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          captureMessage('Stream aborted', { level: 'debug' });
        } else {
          throw error;
        }
      }
    }

    if (enabled) {
      initiateStream();
    }

    return () => {
      ctrl.abort();
    };
  }, [route, enabled]);

  return {
    ...featureCollection,
    features: featureCollection.features
      .filter(({ properties }) => filterType(route, filter, properties))
      .filter(({ properties }) =>
        JSON.stringify(properties).toLowerCase().includes(search.toLowerCase()),
      )
      .sort(featureSortFn(sort)),
    total: featureCollection.features.length,
  };
}
